import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { SaveSsoConfigInput } from '@risksmart-app/trpc/services/service.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export const useSaveSsoConfigurationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.ssoConfiguration.save.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.ssoConfiguration.list.queryKey(),
        });
      },
    }),
  });
  const saveSsoConfiguration = async (input: SaveSsoConfigInput) => {
    const result = await mutation.mutateAsync(input);
    const action = result.Action;

    // `no_change` is confirmed like `created`, deliberately (ENG-616).
    //
    // `SsoSaveAction` has four members and this branched on three, so a save that
    // resolved `no_change` — the service's answer when the stored connection
    // already matches the submitted one — raised NOTHING. No success toast, no
    // error toast, the form just sits there: indistinguishable from a dead
    // button, and it hid a real problem for a whole triage cycle
    // (`ssoConfiguration.spec.ts` "Can save an LDAP SSO configuration with SSO
    // disabled" failed on a missing toast with no other signal anywhere on the
    // page, blocking its two serial dependents).
    //
    // "Saved" is the honest message for both: the user asked for a state and that
    // state is now stored. The Apollo twin carries the same fallthrough and the
    // same fix.
    if (action === 'created' || action === 'no_change') {
      addNotification({
        type: 'success',
        content: 'SSO configuration saved successfully',
      });
      if (result.IsOrgConnected) {
        addNotification({
          type: 'success',
          content: 'SSO connection enabled successfully',
        });
      }
    } else if (action === 'updated_login_experience') {
      addNotification({
        type: 'success',
        content: 'Login experience domains updated successfully',
      });
    } else if (action === 'updated_org_connection') {
      addNotification({
        type: 'success',
        // Distinguish enabled from disabled, as the Apollo twin does (ENG-616).
        // This branch fires for BOTH directions of the org-connection toggle, and
        // a flat "successfully updated" tells the user nothing about which way it
        // went. `ssoConfiguration.spec.ts` "Can enable SSO toggle and re-save"
        // asserts the enabled wording, so V3 would have failed it the moment
        // COI-501 unblocked the chain.
        content: result.IsOrgConnected
          ? 'SSO connection enabled successfully'
          : 'SSO connection disabled successfully',
      });
    }

    return result;
  };

  return {
    saveSsoConfiguration,
    loading: mutation.isPending,
  };
};
